import React, { createContext, useContext, useReducer } from 'react';
import axios from 'axios';

const ProduitContext = createContext();

const initialState = {
  produits: [],
  produit: null,
  loading: false,
  error: null,
  totalPages: 0,
  currentPage: 1,
  totalProduits: 0,
};

const produitReducer = (state, action) => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: true };
    case 'GET_PRODUITS':
      return {
        ...state,
        produits: action.payload.produits,
        totalPages: action.payload.totalPages,
        currentPage: action.payload.currentPage,
        totalProduits: action.payload.totalProduits,
        loading: false,
      };
    case 'GET_PRODUIT':
      return { ...state, produit: action.payload, loading: false };
    case 'ADD_PRODUIT':
      return { ...state, produits: [action.payload, ...state.produits], loading: false };
    case 'UPDATE_PRODUIT':
      return {
        ...state,
        produits: state.produits.map((produit) =>
          produit._id === action.payload._id ? action.payload : produit
        ),
        loading: false,
      };
    case 'DELETE_PRODUIT':
      return {
        ...state,
        produits: state.produits.filter((produit) => produit._id !== action.payload),
        loading: false,
      };
    case 'PRODUIT_ERROR':
      return { ...state, error: action.payload, loading: false };
    case 'CLEAR_ERRORS':
      return { ...state, error: null };
    default:
      return state;
  }
};

export const ProduitProvider = ({ children }) => {
  const [state, dispatch] = useReducer(produitReducer, initialState);

  // Obtenir tous les produits
  const getProduits = async (filters = {}) => {
    dispatch({ type: 'SET_LOADING' });
    try {
      const queryString = new URLSearchParams(filters).toString();
      const res = await axios.get(`/produits?${queryString}`);
      dispatch({ type: 'GET_PRODUITS', payload: res.data });
    } catch (err) {
      dispatch({ type: 'PRODUIT_ERROR', payload: err.response.data.message });
    }
  };

  // Obtenir un produit par ID
  const getProduit = async (id) => {
    dispatch({ type: 'SET_LOADING' });
    try {
      const res = await axios.get(`/produits/${id}`);
      dispatch({ type: 'GET_PRODUIT', payload: res.data });
    } catch (err) {
      dispatch({ type: 'PRODUIT_ERROR', payload: err.response.data.message });
    }
  };

  // Créer un produit
  const createProduit = async (formData) => {
    dispatch({ type: 'SET_LOADING' });
    try {
      const res = await axios.post('/produits', formData);
      dispatch({ type: 'ADD_PRODUIT', payload: res.data });
      return { success: true, message: 'Produit créé avec succès!' };
    } catch (err) {
      dispatch({ type: 'PRODUIT_ERROR', payload: err.response.data.message });
      return { success: false, message: err.response.data.message };
    }
  };

  // Mettre à jour un produit
  const updateProduit = async (id, formData) => {
    dispatch({ type: 'SET_LOADING' });
    try {
      const res = await axios.put(`/produits/${id}`, formData);
      dispatch({ type: 'UPDATE_PRODUIT', payload: res.data });
      return { success: true, message: 'Produit mis à jour!' };
    } catch (err) {
      dispatch({ type: 'PRODUIT_ERROR', payload: err.response.data.message });
      return { success: false, message: err.response.data.message };
    }
  };

  // Supprimer un produit
  const deleteProduit = async (id) => {
    dispatch({ type: 'SET_LOADING' });
    try {
      await axios.delete(`/produits/${id}`);
      dispatch({ type: 'DELETE_PRODUIT', payload: id });
      return { success: true, message: 'Produit supprimé!' };
    } catch (err) {
      dispatch({ type: 'PRODUIT_ERROR', payload: err.response.data.message });
      return { success: false, message: err.response.data.message };
    }
  };

  // Obtenir les produits d'un artisan
  const getProduitsByArtisan = async (artisanId, page = 1) => {
    dispatch({ type: 'SET_LOADING' });
    try {
      const res = await axios.get(`/produits/artisan/${artisanId}?page=${page}`);
      dispatch({ type: 'GET_PRODUITS', payload: res.data });
    } catch (err) {
      dispatch({ type: 'PRODUIT_ERROR', payload: err.response.data.message });
    }
  };

  return (
    <ProduitContext.Provider
      value={{
        ...state,
        getProduits,
        getProduit,
        createProduit,
        updateProduit,
        deleteProduit,
        getProduitsByArtisan,
      }}
    >
      {children}
    </ProduitContext.Provider>
  );
};

export const useProduits = () => {
  const context = useContext(ProduitContext);
  if (!context) {
    throw new Error('useProduits must be used within a ProduitProvider');
  }
  return context;
};

export default ProduitContext;